import {
  hasPermission,
  PERMISSIONS,
  POST_LOGIN_PATH_LOCAL_STORAGE_KEY,
  PREVIEW_QUOTE_CONFIRMATION_PATH,
  QUOTE_CONFIRMATION_PATH
} from '@common/utils'
import { MolsonTypography } from '@components/reusable'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from 'react-oidc-context'
import { Navigate, useLocation } from 'react-router-dom'
import { usePermissionsStore } from '@/stores/permissionsStore'

type PrivateRouteProps = {
  children: ReactNode
  requiredPermissions?: string[]
}

export const PrivateRoute = ({
  children,
  requiredPermissions = [PERMISSIONS.MY_QUOTES, PERMISSIONS.CUSTOMERS]
}: PrivateRouteProps) => {
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth()
  const location = useLocation()

  const isQuoteConfirmation =
    location.pathname === QUOTE_CONFIRMATION_PATH ||
    location.pathname === PREVIEW_QUOTE_CONFIRMATION_PATH

  const { permissions } = usePermissionsStore()

  const { t } = useTranslation()

  const redirectToLogin = () => {
    localStorage.setItem(
      POST_LOGIN_PATH_LOCAL_STORAGE_KEY,
      `${location.pathname}${location.search}`
    )
    return <Navigate replace={true} to="/" />
  }

  if (isQuoteConfirmation) {
    return <>{children}</>
  }

  if (isAuthLoading) {
    return null
  }

  if (!permissions) {
    return isAuthenticated ? null : redirectToLogin()
  }

  if (isAuthenticated) {
    localStorage.removeItem(POST_LOGIN_PATH_LOCAL_STORAGE_KEY)
  } else {
    return redirectToLogin()
  }

  if (!hasPermission(permissions, requiredPermissions)) {
    return (
      <MolsonTypography sx={{ mt: 2, textAlign: 'center' }}>
        {t('restrictedPage')}
      </MolsonTypography>
    )
  }

  return <>{children}</>
}
